import type { JSONConfig } from '@/engine/app/interfaces.ts';
import { SceneItemKind } from '@/engine/app/interfaces.ts';
import { ImagesMode, SliceType } from '@/engine/app/modules/images/interfaces.ts';

export const color = '#00A3FF';

const angleMeasurementRelated = {
  color: '#FF0000',
  highlightColor: '#FFC949',
  selectionColor: '#E5A100'
};

export const angleMeasurementRelatedStub = {
  highlightColor: '',
  selectionColor: ''
};

export const centralLineStub = {
  clDescriptor: ''
};

export const landingZoneArea: JSONConfig.Measurement = {
  descriptor: 'landing_zone_area',
  attachments: ['tp.landing_zone_spline.pts', 'tp.ap_direction', 'landing_zone_spline'],
  label: 'Tricuspid annulus (projected)',
  syncSliceDescriptor: SliceType.SAGITTAL,
  color: '#93ff3c',
  imagesMode: ImagesMode.POLYLINE,
  kind: SceneItemKind.SplineAreaMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varBasalRvAp: JSONConfig.Measurement = {
  descriptor: 'var_basal_rv_ap',
  attachments: ['tp.pt_basal_rv_a', 'tp.pt_basal_rv_p'],
  label: 'AP distance',
  isLabelColored: true,
  syncSliceDescriptor: SliceType.SAGITTAL,
  color: '#FF006C',
  imagesMode: ImagesMode.POLYLINE,
  kind: SceneItemKind.DistanceMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varBasalRvSl: JSONConfig.Measurement = {
  descriptor: 'var_basal_rv_sl',
  attachments: ['tp.pt_basal_rv_s', 'tp.pt_basal_rv_l'],
  label: 'SL distance',
  isLabelColored: true,
  syncSliceDescriptor: SliceType.SAGITTAL,
  color: '#0899FF',
  imagesMode: ImagesMode.POLYLINE,
  kind: SceneItemKind.DistanceMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varBasalRvTvAp: JSONConfig.Measurement = {
  descriptor: 'var_basal_rv_tv_ap',
  attachments: ['tp.pt_basal_rv_tv_a', 'tp.pt_basal_rv_tv_p'],
  label: 'AP distance',
  isLabelColored: true,
  syncSliceDescriptor: SliceType.SAGITTAL,
  color: '#FF006C',
  imagesMode: ImagesMode.POLYLINE,
  kind: SceneItemKind.DistanceMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varBasalRvTvSl: JSONConfig.Measurement = {
  descriptor: 'var_basal_rv_tv_sl',
  attachments: ['tp.pt_basal_rv_tv_s', 'tp.pt_basal_rv_tv_l'],
  label: 'SL distance',
  isLabelColored: true,
  syncSliceDescriptor: SliceType.SAGITTAL,
  color: '#0899FF',
  imagesMode: ImagesMode.POLYLINE,
  kind: SceneItemKind.DistanceMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const tp_Pl_SvcAreaOne: JSONConfig.Measurement = {
  descriptor: 'tp.pl_svc1.area',
  attachments: ['tp.pl_svc1.area.pts', 'tp.pl_svc1'],
  label: 'SVC',
  syncSliceDescriptor: SliceType.AXIAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.AreaMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const tp_Pl_IvcAreaOne: JSONConfig.Measurement = {
  descriptor: 'tp.pl_ivc1.area',
  attachments: ['tp.pl_ivc1.area.pts', 'tp.pl_ivc1'],
  label: 'IVC',
  syncSliceDescriptor: SliceType.AXIAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.AreaMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const tp_Pl_RightFemoralVesselMinArea: JSONConfig.Measurement = {
  descriptor: 'tp.pl_right_femoral_vessel_min.area',
  attachments: ['tp.pl_right_femoral_vessel_min.area.pts', 'tp.pl_right_femoral_vessel_min'],
  label: 'Right minimum',
  syncSliceDescriptor: SliceType.AXIAL,
  color,
  imagesMode: ImagesMode.POLYLINE,
  clDescriptor: 'vessel.right_centerline',
  kind: SceneItemKind.DiameterMeasurement,
  ...angleMeasurementRelatedStub
};

export const tp_Pl_LeftFemoralVesselMinArea: JSONConfig.Measurement = {
  descriptor: 'tp.pl_left_femoral_vessel_min.area',
  attachments: ['tp.pl_left_femoral_vessel_min.area.pts', 'tp.pl_left_femoral_vessel_min'],
  label: 'Left minimum',
  syncSliceDescriptor: SliceType.AXIAL,
  color,
  imagesMode: ImagesMode.POLYLINE,
  clDescriptor: 'vessel.left_centerline',
  kind: SceneItemKind.DiameterMeasurement,
  ...angleMeasurementRelatedStub
};

export const tp_Pl_CustomFemoralVesselMinArea: JSONConfig.Measurement = {
  descriptor: 'tp.pl_femoral_vessel_custom.area',
  attachments: ['tp.pl_femoral_vessel_custom.pts', 'tp.pl_femoral_vessel_custom', 'tp.pl_femoral_vessel_custom.area'],
  label: 'Custom',
  syncSliceDescriptor: SliceType.AXIAL,
  color,
  imagesMode: ImagesMode.POLYLINE,
  kind: SceneItemKind.DiameterMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varRaHeight: JSONConfig.Measurement = {
  descriptor: 'var_ra_height',
  attachments: ['tp.pt_ra_height'],
  label: 'RA Height',
  syncSliceDescriptor: SliceType.CORONAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.DistanceMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varMaxRaHeight: JSONConfig.Measurement = {
  descriptor: 'var_max_ra_height',
  attachments: [],
  label: 'Max RA Height',
  syncSliceDescriptor: SliceType.CORONAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.DistanceMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varRvDepthCoaxial: JSONConfig.Measurement = {
  descriptor: 'var_rv_depth',
  attachments: ['tp.pt_rv_depth'],
  label: 'RV Depth (coaxial)',
  syncSliceDescriptor: SliceType.CORONAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.DistanceMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varRvDepthApex: JSONConfig.Measurement = {
  descriptor: 'var_rv_depth_apex',
  attachments: ['tp.pt_rv_depth_apex'],
  label: 'RV Depth (apex)',
  syncSliceDescriptor: SliceType.CORONAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.DistanceMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varIvcToTva: JSONConfig.Measurement = {
  descriptor: 'var_ivc_to_tva',
  attachments: [],
  label: 'IVC to TVA Distance',
  syncSliceDescriptor: SliceType.CORONAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.DistanceMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varIvcToTvaOffset: JSONConfig.Measurement = {
  descriptor: 'var_ivc_to_tva_offset',
  attachments: [],
  label: 'IVC to TVA Offset',
  syncSliceDescriptor: SliceType.AXIAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.DistanceMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varRaVolume: JSONConfig.Measurement = {
  descriptor: 'var_ra_volume',
  attachments: [],
  label: 'RA Volume',
  syncSliceDescriptor: SliceType.SAGITTAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.VolumeMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varRvVolume: JSONConfig.Measurement = {
  descriptor: 'var_rv_volume',
  attachments: [],
  label: 'RV Volume',
  syncSliceDescriptor: SliceType.SAGITTAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.VolumeMeasurement,
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varPapillaryDistanceCheck: JSONConfig.Measurement = {
  descriptor: 'var_papillary_heights',
  attachments: [],
  label: 'At least 3 conc paps <25 mm',
  syncSliceDescriptor: SliceType.SAGITTAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.FlagMeasurement,
  details: {
    label: [
      { text: 'Success: Yes', icon: 'success' },
      { text: 'Warning: otherwise', icon: 'warning' }
    ]
  },
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varPapillaryDistribution: JSONConfig.Measurement = {
  descriptor: 'var_papillary_distribution',
  attachments: [],
  label: 'Papillary Distribution',
  syncSliceDescriptor: SliceType.SAGITTAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.ValueMeasurement,
  details: {
    units: '/ 4'
  },
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varAnnulusOversize: JSONConfig.Measurement = {
  descriptor: 'var_annulus_oversize',
  attachments: [],
  label: 'TV Annulus Oversize',
  syncSliceDescriptor: SliceType.SAGITTAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.ValueMeasurement,
  details: {
    units: '%',
    factor: 100
  },
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varBasalRvOversize: JSONConfig.Measurement = {
  descriptor: 'var_basal_rv_oversize',
  attachments: [],
  label: 'Basal RV Oversize',
  syncSliceDescriptor: SliceType.SAGITTAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.ValueMeasurement,
  details: {
    units: '%',
    factor: 100
  },
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varRaRvWorkingRoom: JSONConfig.Measurement = {
  descriptor: 'var_ra_rv_working_room',
  attachments: [],
  label: 'Total RA+RV (apex) Working Room',
  syncSliceDescriptor: SliceType.SAGITTAL,
  color,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.ValueMeasurement,
  details: {
    factor: 1,
    units: 'mm'
  },
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varLandingZonePdd: JSONConfig.Measurement = {
  descriptor: 'var_landing_zone_area_dP',
  attachments: [],
  label: 'Perimeter-derived diameter',
  syncSliceDescriptor: SliceType.SAGITTAL,
  color,
  imagesMode: ImagesMode.POLYLINE,
  kind: SceneItemKind.ValueMeasurement,
  details: {
    factor: 1,
    units: 'mm'
  },
  ...angleMeasurementRelatedStub,
  ...centralLineStub
};

export const varCatheterRotationAngle: JSONConfig.Measurement = {
  descriptor: 'var_catheter_rotation_angle',
  attachments: [],
  label: 'Catheter rotation',
  syncSliceDescriptor: SliceType.AXIAL,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.AngleMeasurement,
  ...centralLineStub,
  ...angleMeasurementRelated
};

export const varCatheterSecondaryFlex: JSONConfig.Measurement = {
  descriptor: 'var_catheter_secondary_flex',
  attachments: [],
  label: 'Catheter secondary flex',
  syncSliceDescriptor: SliceType.SAGITTAL,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.AngleMeasurement,
  ...centralLineStub,
  ...angleMeasurementRelated
};

export const varCatheterPrimaryFlex: JSONConfig.Measurement = {
  descriptor: 'var_catheter_primary_flex',
  attachments: [],
  label: 'Catheter primary flex',
  syncSliceDescriptor: SliceType.CORONAL,
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.AngleMeasurement,
  ...centralLineStub,
  ...angleMeasurementRelated
};

export const varCatheterToTvAngle: JSONConfig.Measurement = {
  descriptor: 'var_catheter_to_tv_angle',
  attachments: [],
  label: 'Catheter tip to TV normal',
  imagesMode: ImagesMode.MPR,
  kind: SceneItemKind.AngleMeasurement,
  syncSliceDescriptor: SliceType.NONE,
  ...centralLineStub,
  ...angleMeasurementRelated
};
